// @ts-strict-ignore
import * as React from 'react';
import Dashboard from './Dashboard';
import { Link, matchPath, Redirect, Route, RouteComponentProps, Switch, useHistory } from 'react-router-dom';
import routes from '../routes';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { TRY_LOGIN, TRY_VERIFY_AUTH_CODE, tryLogout } from '../actions/login';
import { RootState } from '../reducers/reducers';
import { getAllowedMobileRoutes, hubSpotUserToken, userIsRunwayAdmin } from '../helpers';
import Spin from 'antd/lib/spin';
import Result from 'antd/lib/result';
import styles from './styles/appStyles';
import * as Storage from '../helpers/localStorageHelper';
import SilentRefreshContainer from './SilentRefreshContainer';
import { INACTIVITY_PERIOD_MS, INACTIVITY_PERSON_MS_RUNWAY_ADMINS, STAPE_ID_COOKIE_KEY } from '../constants';
import { TRY_REFRESH_AUTH_TOKEN, tryRefreshAuthToken } from '../actions/auth';
import IdleTimer from 'react-idle-timer';
import TrialBanner from './TrialBanner';
import PrivateAppRoute from './PrivateAppRoute';
import { selectOrg, selectIsAppsDemoModeEnabled, selectIsDemoInstance } from '../selectors/selectors';
import { TRY_FETCH_STATE, tryFetchState } from '../actions/state';
import AuthContainer from './login-signup/AuthContainer';
import PaywallPage from './Paywall';
import DemoModeBanner from './dashboard/DemoModeBanner';
import OnboardingContainer from './onboarding/OnboardingContainer';
import { dayjs } from '@/extendedDayjs';
import Text from 'antd/lib/typography/Text';
import { globalMessage } from '../utils/globalMessage';
import { setAppsDemoModeData } from '../actions/ui';
import { createAppsDemoModeData } from '../helpers/demoHelpers';
import { SubscriptionStatus } from '../types';
import MobileLandingComponent from './MobileLandingComponent';
import { isMobileOnly } from 'react-device-detect';
import BuildDetailsComponent from './buildInstallation/BuildDetailsComponent';
import { createAppBuildsRoute } from '../helpers/routeHelpers';
import qs from 'query-string';
import DemoInstanceAnnouncementBanner from './dashboard/DemoInstanceAnnouncementBanner';
import TrialActivation from './dashboard/TrialActivation';
import { UserUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import * as Cookie from 'cookie';
import { PageViewTrackingWrapper } from '@/helpers/PageViewTrackingWrapper';
import { ReleaseRouteComponentMatchParams } from '@/typescriptHelpers';
import { ValidatedAndLoadedReleaseWrapper } from '@/components/ValidatedRouteComponents';

const Authenticating = () => {
    return (
        <div style={{ ...styles.appContainer, ...styles.centerVerticalAndHorizontal }}>
            <Spin tip="Authenticating..." size="large">
                <div
                    style={{
                        width: 100,
                    }}
                />
            </Spin>
        </div>
    );
};

const AppComponent: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const cookies = Cookie.parse(document.cookie);

    const [refreshTokenAttempted, setRefreshTokenAttempted] = React.useState(false);

    const allowedMobileRoutes = getAllowedMobileRoutes();
    const match = matchPath(history.location.pathname, {
        path: allowedMobileRoutes,
    });
    const pathMatchesAllowedMobileRoute = !!match;

    const isViewingInAppsDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    const refreshTokenIsLoading = useIsLoading(TRY_REFRESH_AUTH_TOKEN);
    const refreshTokenSuccess = useUpdated(TRY_REFRESH_AUTH_TOKEN);

    const stateFetchIsLoading = useIsLoading(TRY_FETCH_STATE);
    const stateFetchError = useError(TRY_FETCH_STATE);

    const loginSuccess = useUpdated(TRY_LOGIN);
    const twoFactorLoginSuccess = useUpdated(TRY_VERIFY_AUTH_CODE);
    const signUpSuccess = useUpdated(UserUpdatable.TRY_SIGN_UP);

    const { currentOrgId, currentOrgIdParam, initialAppIdParam, shouldShowTrialBanner, isPaywalled, isDemoInstance } =
        useSelector((state: RootState) => {
            const currentOrg = selectOrg(state);
            const currentOrgId = currentOrg?.id;
            const currentOrgIdParam = currentOrg?.orgId;
            const isDemoInstance = currentOrg ? selectIsDemoInstance(state, currentOrg.id) : false;

            let initialAppIdParam: string = null;

            if (!isDemoInstance) {
                const initialAppIdFromStorage = Storage.getDefaultAppIdParamInOrg(currentOrgId);

                if (initialAppIdFromStorage && currentOrgIdParam) {
                    const appIdBelongsToOrg = state.uuidMappings[currentOrgIdParam].apps[initialAppIdFromStorage];
                    initialAppIdParam = appIdBelongsToOrg ? initialAppIdFromStorage : null;
                }

                if (!initialAppIdParam) {
                    // default to first app in org
                    const initialAppId = currentOrgId ? state.relationships.appsInOrg[currentOrgId].appIds[0] : null;
                    initialAppIdParam = initialAppId ? state.entities.apps.byId[initialAppId].appId : null;
                }
            } else if (state.ui.uiConfig.demoData) {
                initialAppIdParam = 'appollo-ios';
            }

            return {
                currentOrgId,
                currentOrgIdParam,
                initialAppIdParam,
                shouldShowTrialBanner: currentOrg?.subscriptionInfo.shouldShowTrialBanner ?? false,
                isPaywalled: currentOrg?.subscriptionInfo.subscriptionStatus === SubscriptionStatus.Paywall,
                isDemoInstance,
            };
        }, shallowEqual);

    const {
        userId,
        isLoggedIn,
        isLoggedInAndEmailVerified,
        defaultOrgIdForUserId,
        inactivityTimeoutPeriod,
        isEmailVerificationRequired,
        is2FARequired,
        isRunwayAdmin,
        isStateFetched,
    } = useSelector((state: RootState) => {
        const userId = state.auth?.authCredentials?.userId;
        const isLoggedIn = state.auth?.isLoggedIn;
        const isLoggedInAndEmailVerified = isLoggedIn && state.auth.isEmailVerified;
        const defaultOrgId = userId ? Storage.getDefaultOrgForUser(userId) : null;
        const isRunwayAdmin = state.user ? userIsRunwayAdmin(state.user) : false;
        const inactivityTimeoutPeriod = isRunwayAdmin ? INACTIVITY_PERSON_MS_RUNWAY_ADMINS : INACTIVITY_PERIOD_MS;
        const isEmailVerificationRequired = state.auth ? !state.auth.isEmailVerified : false;
        const is2FARequired = state.auth ? state.auth.twoFactorAuthRequired : false;
        const isStateFetched = !!state.user;

        return {
            userId: state.auth?.userId,
            isLoggedIn,
            isLoggedInAndEmailVerified,
            defaultOrgIdForUserId: defaultOrgId,
            inactivityTimeoutPeriod,
            isEmailVerificationRequired,
            is2FARequired,
            isRunwayAdmin,
            isStateFetched,
        };
    }, shallowEqual);

    const handleInactivity = () => {
        if (isLoggedIn) {
            dispatch(tryLogout());

            globalMessage.warning({
                duration: 300, // 5 minutes
                content: (
                    <Text>
                        You have been logged out after{' '}
                        {dayjs.duration(inactivityTimeoutPeriod, 'milliseconds').asHours()} hours of inactivity
                    </Text>
                ),
            });
        }
    };

    const showDemoInstanceAnnouncementBanner = isDemoInstance && false; // not currently needed. TODO add to Config object returned from BE

    const qParams = qs.parse(history.location.search);
    const accessToken = qParams?.accessToken as string;

    React.useEffect(() => {
        const needsUrlReplace = accessToken && isLoggedIn;

        if (needsUrlReplace) {
            // clear access token query param if present once logged in
            const sanitizedCurrentUrl = qs.exclude(window.location.href, ['accessToken']);
            const currentUrl = new URL(sanitizedCurrentUrl);

            const sanitizedCurrentLocation = {
                pathname: currentUrl.pathname,
                search: currentUrl.search,
                state: history.location.state,
            };

            history.replace(sanitizedCurrentLocation);
        }
    }, [isLoggedIn]);

    React.useEffect(() => {
        dispatch(tryRefreshAuthToken(accessToken ?? null));

        setRefreshTokenAttempted(true);
    }, []);

    React.useEffect(() => {
        const isLoginSuccess = loginSuccess && !is2FARequired;
        const isSignUpSuccess = signUpSuccess && !is2FARequired;
        const shouldFetchState = isLoginSuccess || refreshTokenSuccess || twoFactorLoginSuccess || isSignUpSuccess;

        if (shouldFetchState && !isEmailVerificationRequired) {
            // Extract orgIdParam from the current URL path if present.
            // This ensures each tab uses the org from its URL on refresh, rather than
            // a shared localStorage value that could be overwritten by another tab.
            const orgMatch = matchPath<{ orgIdParam?: string }>(window.location.pathname, {
                path: routes.org,
            });
            const orgIdFromUrl = orgMatch?.params?.orgIdParam;

            // Prefer URL-based orgId over localStorage to support multi-tab isolation
            const orgIdForFetch = orgIdFromUrl || defaultOrgIdForUserId;

            dispatch(
                tryFetchState({
                    orgId: orgIdForFetch,
                    userId,
                    stapeId: cookies[STAPE_ID_COOKIE_KEY],
                    hubspotUserToken: hubSpotUserToken(document),
                }),
            );
        }
    }, [loginSuccess, signUpSuccess, refreshTokenSuccess, twoFactorLoginSuccess]);

    React.useEffect(() => {
        if (stateFetchError) {
            globalMessage.error(stateFetchError);

            console.debug('[REDIRECT DEBUG]', 'stateFetchError', stateFetchError);
            history.replace(routes.login);
        }
    }, [stateFetchError]);

    React.useEffect(() => {
        if (isLoggedIn && isStateFetched && isDemoInstance) {
            // force demo mode for demo instance
            const demoData = createAppsDemoModeData();

            dispatch(setAppsDemoModeData(demoData));
        }
    }, [isLoggedIn, isDemoInstance, isStateFetched]);

    const showDemoInstanceBanner = isDemoInstance ? isLoggedIn && !isRunwayAdmin : isViewingInAppsDemoMode;

    if (refreshTokenIsLoading || stateFetchIsLoading) {
        return <Authenticating />;
    }

    // redirect disallowed mobile routes
    if (isLoggedInAndEmailVerified && isStateFetched && isMobileOnly && !pathMatchesAllowedMobileRoute) {
        const defaultMobileRoute =
            currentOrgIdParam && initialAppIdParam ?
                createAppBuildsRoute(currentOrgIdParam, initialAppIdParam)
            :   routes.mobile;

        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            window.location.pathname,
            '-> next route:',
            defaultMobileRoute,
            '(mobile route redirect in App.tsx)',
        );
        return <Redirect to={{ pathname: defaultMobileRoute }} />;
    }

    if (!refreshTokenAttempted) {
        return null;
    }

    return (
        <>
            {currentOrgId && (
                <>
                    {shouldShowTrialBanner && <TrialBanner orgId={currentOrgId} />}
                    {showDemoInstanceAnnouncementBanner && <DemoInstanceAnnouncementBanner />}
                    {showDemoInstanceBanner && <DemoModeBanner isDemoInstance={isDemoInstance} orgId={currentOrgId} />}
                    {<TrialActivation orgId={currentOrgId} />}
                </>
            )}
            <SilentRefreshContainer />
            <IdleTimer timeout={inactivityTimeoutPeriod} onIdle={handleInactivity} />
            <Switch>
                <Route
                    path={[routes.login, routes.signup, routes.qsciSignup, routes.buildDistroSignup]}
                    component={AuthContainer}
                />
                <Route path={routes.mobile} component={MobileLandingComponent} />
                {isPaywalled && !isRunwayAdmin && <PrivateAppRoute path={routes.dashboard} component={PaywallPage} />}
                <PrivateAppRoute
                    path={routes.buildDetails}
                    render={() => {
                        return (
                            <Route
                                path={routes.buildDetails}
                                render={(routeProps: RouteComponentProps) => {
                                    const releaseRouteProps =
                                        routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                    return (
                                        <ValidatedAndLoadedReleaseWrapper
                                            {...releaseRouteProps}
                                            showNotFoundComponent
                                            withTracking={false}
                                        >
                                            {(validatedReleaseRouteProps) => {
                                                return (
                                                    <PageViewTrackingWrapper pageTitle={'Build Details'}>
                                                        <BuildDetailsComponent
                                                            {...releaseRouteProps}
                                                            {...validatedReleaseRouteProps}
                                                        />
                                                    </PageViewTrackingWrapper>
                                                );
                                            }}
                                        </ValidatedAndLoadedReleaseWrapper>
                                    );
                                }}
                            />
                        );
                    }}
                />
                <PrivateAppRoute
                    path={routes.releases}
                    render={(routeProps: RouteComponentProps) => <Dashboard {...routeProps} />}
                />
                <PrivateAppRoute
                    path={routes.dashboard}
                    render={(routeProps: RouteComponentProps) => <Dashboard {...routeProps} />}
                    defaultSelectedOrgIdParam={currentOrgIdParam}
                    defaultSelectedAppIdParam={initialAppIdParam}
                />
                <PrivateAppRoute
                    exact
                    path={[routes.home]}
                    component={Dashboard}
                    defaultSelectedOrgIdParam={currentOrgIdParam}
                    defaultSelectedAppIdParam={initialAppIdParam}
                />
                {!refreshTokenIsLoading && (
                    <Route path={'/'}>
                        <Result
                            status="404"
                            title="404"
                            subTitle="Whoops, you're in the wrong place."
                            extra={<Link to={routes.home}>Go Home</Link>}
                            style={styles.base}
                        />
                    </Route>
                )}
            </Switch>
            {currentOrgId && <OnboardingContainer orgId={currentOrgId} />}
        </>
    );
};

export default AppComponent;
